import { Fragment, ReactElement, ReactNode } from 'react';
import { Row, Table } from '@tanstack/react-table';

import { Tbody, TbodyProps } from '@oaknorth/component-core-v2';

import { TableEmptyState } from '../TableEmptyState';
import { TableRow } from '../TableRow';

export interface TableBodyProps<T extends object> extends TbodyProps {
  table: Table<T>;

  /**
   * Callback to execute when the user click on a row. Row is a wrapper with additional props,
   * the data can be accessed on 'row.original'.
   */
  onClickRow?: (row: Row<T>, event: React.MouseEvent) => void;

  /**
   * Render function for sub row when using custom expandable rows (not hierarchical rows)
   */
  renderExpandedRow?: ({ row }: { row: Row<T> }) => ReactElement | null;

  /**
   * By default, borders between rows are done as bottom borders on cells
   *
   * If this is true, we use top borders instead. This is useful when doing e.g. expandable rows
   * because the subrows don't end up separated from their parents.
   */
  useTopBorders?: boolean;
  emptyResult?: ReactNode;
}

export const TableBody = <T extends object = {}>({
  table,
  onClickRow,
  renderExpandedRow,
  useTopBorders,
  css,
  emptyResult,
  ...props
}: TableBodyProps<T>) => {
  const rows = table.getRowModel().rows;

  return (
    <Tbody
      css={{
        ...(useTopBorders
          ? {
              // th included in case we're using th inside tbody for row headers
              'td, th': {
                borderBottomWidth: '0',
                borderTopWidth: '1px',
              },
            }
          : undefined),
        ...css,
      }}
      {...props}
    >
      {rows.length > 0 ? (
        rows.map((row) => {
          return (
            <Fragment key={row.id}>
              <TableRow row={row} onClick={onClickRow} table={table} />
              {renderExpandedRow && renderExpandedRow({ row })}
            </Fragment>
          );
        })
      ) : (
        <TableEmptyState table={table} emptyResult={emptyResult} />
      )}
    </Tbody>
  );
};
